import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { YES_NO_OPTIONS, type HiAttemptFormValues } from '../hiAttempt.schema';

const DISABLED_STYLE = 'disabled:opacity-100 text-black';

type Props = {
  readOnly?: boolean;
};

const TenantYesNoRadio = ({
  name,
  label,
  readOnly,
}: {
  name: string;
  label: string;
  readOnly?: boolean;
}) => {
  const { control } = useFormContext<HiAttemptFormValues>();
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      <Controller
        control={control}
        name={name as `tenants.${number}.relative` | `tenants.${number}.lease`}
        render={({ field }) => (
          <RadioGroup
            value={field.value ?? ''}
            onValueChange={(v) => field.onChange(v as 'YES' | 'NO')}
            disabled={readOnly}
            className="flex flex-row gap-6 h-10 items-center"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="YES" id={`${name}-yes`} />
              <Label htmlFor={`${name}-yes`} className="cursor-pointer font-normal">
                Yes
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="NO" id={`${name}-no`} />
              <Label htmlFor={`${name}-no`} className="cursor-pointer font-normal">
                No
              </Label>
            </div>
          </RadioGroup>
        )}
      />
    </div>
  );
};

export const HiAttemptStep2TenantInfo = ({ readOnly }: Props = {}) => {
  const { control } = useFormContext<HiAttemptFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'tenants' });

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="tenantOccupied"
          options={YES_NO_OPTIONS}
          placeholder="Select"
          label="Tenant Occupied"
          disabled={readOnly}
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <span className="text-sm font-semibold text-primary">Tenants</span>
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              className="text-primary border-primary hover:bg-[#EDF5FF] h-8 px-3 text-sm"
              onClick={() => append({ name: '', floor: null, relative: null, lease: null, rentAmount: null })}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Tenant
            </Button>
          )}
        </div>

        {fields.map((field, index) => (
          <div key={field.id} className="flex flex-col gap-4 p-4 bg-[#F5F5F5] rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <FormFieldComponent
                control={control}
                name={`tenants.${index}.name`}
                label="Tenant Name"
                placeholder="Tenant Name"
                required
                disabled={readOnly}
                disabledStyle={readOnly ? DISABLED_STYLE : undefined}
              />
              <FormFieldComponent
                control={control}
                name={`tenants.${index}.floor`}
                label="Floor"
                placeholder="e.g. 2A"
                disabled={readOnly}
                disabledStyle={readOnly ? DISABLED_STYLE : undefined}
              />
              {!readOnly && (
                <div className="flex items-end pb-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="text-error border-error hover:bg-red-50 h-9 px-3"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <TenantYesNoRadio
                name={`tenants.${index}.relative`}
                label="Relative"
                readOnly={readOnly}
              />
              <TenantYesNoRadio
                name={`tenants.${index}.lease`}
                label="Lease"
                readOnly={readOnly}
              />
              <FormFieldComponent
                control={control}
                name={`tenants.${index}.rentAmount`}
                label="Rent Amount"
                placeholder="0.00"
                type="number"
                allowNegativeNumbers={false}
                maxDecimalPlaces={2}
                disabled={readOnly}
                disabledStyle={readOnly ? DISABLED_STYLE : undefined}
              />
            </div>
          </div>
        ))}

        {fields.length === 0 && (
          <div className="text-sm text-placeholder text-center py-2">No tenants added</div>
        )}
      </div>
    </div>
  );
};
